import { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  element: React.ReactElement;
  hasPermission: boolean;
  redirectTo?: string;
}

/**
 * A component that conditionally renders based on user permissions.
 * If the user lacks permission, they are redirected to a specified path.
 * This should be used directly as the element prop of a Route component.
 *
 * @param element - The element to render when user has permission
 * @param hasPermission - Whether the user has permission to access this route
 * @param redirectTo - Path to redirect to when user lacks permission (default: '/')
 * @returns The element or a Navigate component for redirection
 *
 * @example
 * ```tsx
 * <Route
 *   path="/admin"
 *   element={
 *     <ProtectedRoute
 *       element={<AdminPanel />}
 *       hasPermission={user.isAdmin}
 *       redirectTo="/login"
 *     />
 *   }
 * />
 * ```
 */
const ProtectedRoute = ({
  element,
  hasPermission,
  redirectTo = '/',
}: ProtectedRouteProps): ReactElement => {
  return hasPermission ? element : <Navigate to={redirectTo} replace />;
};

export default ProtectedRoute;
